import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { analysisService } from '../../services/analysisService';
import { corpusService } from '../../services/corpusService';
import toast from 'react-hot-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import DocumentList from '../../components/analysis/DocumentList';
import { AnalysisProgressBar } from '../../components/analysis/AnalysisProgressBar';
import { generateUUID } from '../../utils/uuid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileAlt,
  faBalanceScale,
  faCoins,
  faBuilding,
  faGraduationCap,
  faIndustry,
  faGlobe,
  faLaptopCode,
  faHospital,
  faFileUpload,
  faFilePdf,
  faFileWord,
  faFileLines,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { CorpusSector } from '../../types';

// Map sector codes to Font Awesome icons
const getSectorIcon = (sector: CorpusSector) => {
  const iconMap: Record<string, any> = {
    'GENERAL': faFileAlt,
    'LEGAL': faBalanceScale,
    'FINANCE': faCoins,
    'finanzas': faCoins,
    'ADMIN': faBuilding,
    'HEALTH': faHospital,
    'EDUCATION': faGraduationCap,
    'INDUSTRY': faIndustry,
    'TECH': faLaptopCode,
  };

  return iconMap[sector.code] || faGlobe;
};

// Get file icon based on extension
const getFileIcon = (filename: string) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return faFilePdf;
  if (ext === 'docx' || ext === 'doc') return faFileWord;
  if (ext === 'txt') return faFileLines;
  return faFileAlt;
};

export default function DocumentAnalysisPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState('new');
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    sectorIds: [] as number[],
    title: '',
  });

  const { data: sectorsData } = useQuery({
    queryKey: ['sectors'],
    queryFn: corpusService.getSectors,
  });

  // Find General sector ID and initialize it
  const generalSector = sectorsData?.sectors.find(s => s.code === 'GENERAL');
  const generalSectorId = generalSector?.id;

  // Auto-select General sector when it's loaded
  useEffect(() => {
    if (generalSectorId && !formData.sectorIds.includes(generalSectorId)) {
      setFormData(prev => ({
        ...prev,
        sectorIds: [generalSectorId]
      }));
    }
  }, [generalSectorId]);

  const analyzeMutation = useMutation({
    mutationFn: analysisService.analyzeDocument,
    onSuccess: (_data) => {
      // Progress bar will handle navigation on completion
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al analizar el documento');
      setCurrentSessionId(null);
    },
  });

  const handleAnalysisComplete = (analysisId: number) => {
    toast.success('Documento analizado correctamente');
    setCurrentSessionId(null);
    setSelectedFile(null);
    setFormData({ sectorIds: generalSectorId ? [generalSectorId] : [], title: '' });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    // Navigate directly to sections list instead of global analysis
    navigate(`/analysis/document/${analysisId}/sections`);
  };

  const toggleSector = (sectorId: number) => {
    // Prevent deselecting General sector
    if (sectorId === generalSectorId) {
      return;
    }

    setFormData(prev => {
      const isSelected = prev.sectorIds.includes(sectorId);
      return {
        ...prev,
        sectorIds: isSelected
          ? prev.sectorIds.filter(id => id !== sectorId)
          : [...prev.sectorIds, sectorId]
      };
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
        if (!formData.title) {
          setFormData(prev => ({ ...prev, title: file.name }));
        }
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
        if (!formData.title) {
          setFormData(prev => ({ ...prev, title: file.name }));
        }
      }
    }
  };

  const validateFile = (file: File): boolean => {
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'text/plain',
      'text/tab-separated-values',
      'text/tsv'
    ];

    // También validar por extensión para TSV ya que algunos navegadores no reconocen el MIME type correcto
    const fileName = file.name.toLowerCase();
    const extension = fileName.split('.').pop();
    const allowedExtensions = ['pdf', 'docx', 'doc', 'txt', 'tsv'];

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(extension || '')) {
      toast.error('Tipo de archivo no soportado. Solo se permiten PDF, DOCX, TXT y TSV');
      return false;
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('El archivo es demasiado grande. Tamaño máximo: 10MB');
      return false;
    }

    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedFile) {
      toast.error('Debes seleccionar un archivo');
      return;
    }

    if (formData.sectorIds.length < 2) {
      toast.error('Debes seleccionar al menos un sector adicional además de General');
      return;
    }

    // Generate session ID for progress tracking
    const sessionId = generateUUID();
    setCurrentSessionId(sessionId);

    const submitFormData = new FormData();
    submitFormData.append('file', selectedFile);
    submitFormData.append('sectorIds', JSON.stringify(formData.sectorIds));
    submitFormData.append('sessionId', sessionId);
    if (formData.title) {
      submitFormData.append('title', formData.title);
    }

    analyzeMutation.mutate(submitFormData);
  };

  const removeFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-purple-600 rounded-xl p-8 shadow-lg">
        <h1 className="text-3xl font-bold mb-2 text-white">Análisis de Documentos</h1>
        <p className="text-white/90">
          Sube un documento (PDF, DOCX, TXT) para analizar su contenido por secciones
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="new">Nuevo Análisis</TabsTrigger>
          <TabsTrigger value="history">Historial</TabsTrigger>
        </TabsList>

        <TabsContent value="new" className="mt-6">
          {/* Progress Bar */}
          {currentSessionId && (
            <AnalysisProgressBar
              sessionId={currentSessionId}
              onComplete={handleAnalysisComplete}
            />
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Sector Selection */}
            <Card>
              <CardHeader>
                <CardTitle>Seleccionar Sectores *</CardTitle>
                <CardDescription>
                  Elige uno o varios sectores para analizar el documento con vocabulario especializado.
                  {formData.sectorIds.length >= 2 ? (
                    <span className="block mt-1 text-primary font-medium">
                      {formData.sectorIds.length} sectores seleccionados (General + {formData.sectorIds.length - 1} adicional{formData.sectorIds.length > 2 ? 'es' : ''})
                    </span>
                  ) : (
                    <span className="block mt-1 text-destructive font-medium">
                      Debes seleccionar al menos un sector adicional
                    </span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {sectorsData?.sectors
                    .slice()
                    .sort((a, b) => {
                      // General always first
                      if (a.code === 'GENERAL') return -1;
                      if (b.code === 'GENERAL') return 1;
                      return a.name.localeCompare(b.name);
                    })
                    .map((sector) => {
                      const isSelected = formData.sectorIds.includes(sector.id);
                      const isGeneral = sector.code === 'GENERAL';
                      return (
                        <button
                          key={sector.id}
                          type="button"
                          onClick={() => toggleSector(sector.id)}
                          disabled={isGeneral}
                          className={`p-5 rounded-lg border-2 transition-all hover:shadow-md relative ${
                            isSelected
                              ? 'border-primary bg-primary/10 shadow-md ring-2 ring-primary/20'
                              : 'border-border hover:border-primary/50 bg-card'
                          } ${isGeneral ? 'cursor-not-allowed opacity-90' : 'cursor-pointer'}`}
                        >
                          {isSelected && (
                            <div className="absolute top-2 right-2 bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                              ✓
                            </div>
                          )}
                          {isGeneral && (
                            <div className="absolute top-2 left-2 bg-blue-500 text-white rounded-full px-2 py-0.5 text-[10px] font-bold">
                              OBLIGATORIO
                            </div>
                          )}
                          <div className={`text-3xl mb-2 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`}>
                            <FontAwesomeIcon icon={getSectorIcon(sector)} />
                          </div>
                          <div className={`font-semibold text-sm ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                            {sector.name}
                          </div>
                        </button>
                      );
                    })}
                </div>
              </CardContent>
            </Card>

            {/* File Upload */}
            <Card>
              <CardHeader>
                <CardTitle>Subir Documento *</CardTitle>
                <CardDescription>
                  Formatos soportados: PDF, DOCX, TXT, TSV (máx. 10MB)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!selectedFile ? (
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-all ${
                      isDragging
                        ? 'border-primary bg-primary/5 scale-105'
                        : 'border-border hover:border-primary hover:bg-accent'
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <FontAwesomeIcon icon={faFileUpload} className="text-6xl text-muted-foreground mb-4" />
                    <p className="text-lg font-semibold mb-2">
                      Arrastra un archivo aquí o haz clic para seleccionar
                    </p>
                    <p className="text-sm text-muted-foreground">
                      PDF, DOCX, TXT, TSV • Máximo 10MB
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.docx,.doc,.txt,.tsv"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-4 p-6 bg-accent rounded-lg border-2 border-primary">
                    <div className="text-4xl text-primary">
                      <FontAwesomeIcon icon={getFileIcon(selectedFile.name)} />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-lg">{selectedFile.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={removeFile}
                    >
                      <FontAwesomeIcon icon={faTrash} className="mr-2" />
                      Eliminar
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Title */}
            <Card>
              <CardContent className="pt-6">
                <label className="block text-sm font-medium mb-2">Título del análisis (opcional)</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent bg-background"
                  placeholder="Ej: Manual de usuario"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                />
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex gap-4">
              <Button
                type="submit"
                className="flex-1"
                disabled={analyzeMutation.isPending || !selectedFile || formData.sectorIds.length < 2}
              >
                {analyzeMutation.isPending ? 'Analizando documento...' : 'Analizar Documento'}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/')}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <DocumentList />
        </TabsContent>
      </Tabs>
    </div>
  );
}
